import { isStopActive, RouteStop } from "@/reducers/routes2gSlice"

/** Shared with the mobile client, so both flip together. */
export const STOP_TO_STOP_DISTANCE_FLAG = "stop-to-stop-distance"

export type DisplayedStopDistance = {
  /** Unrounded, so callers can format without drift. */
  distanceMeters: number
  isApproximate: boolean
}

/** No proximity status at all means a temporary stop, not a passed one. */
const isStopPassed = (stop: RouteStop): boolean =>
  stop.proximity_status !== undefined && !isStopActive(stop)

/**
 * Passthrough waypoints would split a leg in two, and a passed stop would
 * measure from history instead of from the vehicle.
 */
const findPredecessor = (
  stops: RouteStop[],
  index: number,
): RouteStop | undefined => {
  for (let i = index - 1; i >= 0; i--) {
    const candidate = stops[i]
    if (candidate.action === "passthrough") continue
    if (isStopPassed(candidate)) continue
    return candidate
  }

  return undefined
}

/**
 * The distance to show next to a stop.
 *
 * `remaining_distance` is cumulative — metres from the vehicle to that stop.
 * With `showLegDistance` on, a stop shows the leg ending at it instead. Legs
 * telescope, so they still add up to the route total.
 *
 * Falls back to the cumulative distance when no leg can be derived.
 *
 * @returns undefined when the stop has no distance at all
 */
export const getDisplayedStopDistance = (
  stops: RouteStop[],
  index: number,
  showLegDistance: boolean,
): DisplayedStopDistance | undefined => {
  const stop = stops[index]
  if (!stop || stop.remaining_distance === undefined) return undefined

  const cumulative: DisplayedStopDistance = {
    distanceMeters: stop.remaining_distance,
    isApproximate: Boolean(stop.isApproximate),
  }

  if (!showLegDistance) return cumulative

  const predecessor = findPredecessor(stops, index)
  if (!predecessor || predecessor.remaining_distance === undefined) {
    return cumulative
  }

  // Metres, never the rounded kilometres on screen: those drift by up to a
  // kilometre per leg. Same reasoning as computeApproachDistance.
  const leg = stop.remaining_distance - predecessor.remaining_distance
  if (leg < 0) return cumulative

  return {
    distanceMeters: leg,
    isApproximate:
      Boolean(stop.isApproximate) || Boolean(predecessor.isApproximate),
  }
}

export const formatStopDistance = (
  distance: DisplayedStopDistance,
  locale: string,
): string => {
  const formatted =
    distance.distanceMeters >= 1000
      ? `${Math.floor(distance.distanceMeters / 1000).toLocaleString(locale)} km`
      : `${distance.distanceMeters} m`

  return distance.isApproximate ? `≈${formatted}` : formatted
}
